/* The one error type the app throws, and the one question worth asking about
 * it.
 *
 * Split out of `api.ts` rather than living in it, for a reason that is about
 * testing and not about tidiness. `say.ts` has to name a sentence for every
 * status, and the unit tests compile `say.ts` under Node, where there is no
 * `Response`, no `fetch` and no `FormData`. Keeping the class here means the
 * sentences can be tested without a browser and without a mock.
 *
 * Nothing in this file touches the DOM.
 */

/** Status 0: the request never reached the service. Not a code the service can
 *  return, which is why it is safe to use for the case where there was nothing
 *  on the other end to return one. */
export const UNREACHED = 0;

export class ServiceError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
    /** Seconds, from a 429's `Retry-After` header or a `retry_after` body
     *  field. Present only when the service said so. */
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = "ServiceError";
  }
}

/** Whether repeating the same request could succeed.
 *
 *  Transient: it never landed, the service is failing (5xx), or the service
 *  asked us to wait (408, 429). Terminal: everything else, which is a
 *  statement about the request rather than about the moment, and repeating it
 *  changes nothing. Before this distinction existed, one dropped packet during
 *  a read left a page marked "reading" for as long as the tab lived. */
export function isTransient(err: unknown): boolean {
  if (!(err instanceof ServiceError)) return false;
  return err.status === UNREACHED || err.status >= 500 ||
    err.status === 408 || err.status === 429;
}
